/**
 * Unified input: XR controllers, tracked hands and the desktop mouse all become "pointers"
 * with a world-space ray, select/squeeze state and (for controllers) thumbstick axes.
 * Controllers are drawn procedurally (no profile CDN fetch); hands use XRHandModelFactory.
 *
 * Apple Vision Pro (2026-10-07) selects with gaze and pinch: a transient pointer appears when
 * the fingers meet, its ray along the gaze, fires select, follows the hand while held and is
 * gone on release. It has no buttons, so holding a pinch on something counts as the grip
 * (`HOLD_TO_GRIP_MS`), which is how a panel, a patient or a model is carried there.
 */
import * as THREE from 'three';
import { XRHandModelFactory } from 'three/examples/jsm/webxr/XRHandModelFactory.js';
import type { PlayerRig } from './PlayerRig.ts';

export type PointerKind = 'controller' | 'hand' | 'mouse';

export interface Pointer {
  id: string;
  kind: PointerKind;
  handedness: 'left' | 'right' | 'none';
  ray: THREE.Ray;
  selecting: boolean;
  squeezing: boolean;
  /** xr-standard thumbstick, -1..1, y negative = forward. */
  axes: { x: number; y: number };
  buttons: { a: boolean; b: boolean; stick: boolean; x: boolean };
  /** Analog trigger and grip, 0..1 (controllers). */
  trigger: number;
  grip: number;
  /** World position of the index finger tip when hand tracked. */
  indexTip?: THREE.Vector3;
  /** Set by the Interactive system: where the ray currently hits something. */
  hitDistance?: number;
  active: boolean;
  /** A gaze-and-pinch pointer (Apple Vision Pro): exists only while the pinch is held. */
  transient?: boolean;
  /** The input has buttons (a controller), so its menu button can open the wrist menu. */
  gamepad?: boolean;
  /** When a gaze-and-pinch began and whether it began on something interactive. */
  pinchStart?: { at: number; onItem: boolean };
  /** Visuals: ray line and cursor dot. */
  line?: THREE.Line;
  cursor?: THREE.Mesh;
}

export interface HandPoseSample {
  handedness: Pointer['handedness'];
  kind: 'controller' | 'hand';
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  pinch: number;
  curl: [number, number, number, number, number];
  /** The pointer this hand drives (for gesture to pointer mapping). */
  pointerId: string;
  /** World position of the thumb tip when hand tracked. */
  thumbTip?: THREE.Vector3;
}

export type PointerEventName = 'selectstart' | 'selectend' | 'squeezestart' | 'squeezeend';
type PointerListener = (pointer: Pointer) => void;
type KeyListener = (event: KeyboardEvent) => void;

interface ControllerSlot {
  index: number;
  controller: THREE.Group; // target ray space
  grip: THREE.Group;
  hand: THREE.Group;
  pointer: Pointer;
  inputSource: XRInputSource | undefined;
  controllerModel: THREE.Group;
  handModel: THREE.Object3D | undefined;
}

const RAY_LENGTH = 6;
/**
 * Controller slots, shared by controllers and hands. With only two, a browser that reports
 * both hands and gaze-and-pinch pointers fills them with the hands, and three.js drops the
 * pinch pointers silently.
 */
const SLOTS = 4;
/** A gaze-and-pinch held this long on something interactive becomes a grip. */
export const HOLD_TO_GRIP_MS = 550;

function makeRayLine(colour: number): THREE.Line {
  const geometry = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, 0, -1),
  ]);
  const material = new THREE.LineBasicMaterial({ color: colour, transparent: true, opacity: 0.6 });
  const line = new THREE.Line(geometry, material);
  line.name = 'pointer-ray';
  line.scale.z = RAY_LENGTH;
  return line;
}

function makeCursor(colour: number): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.RingGeometry(0.008, 0.016, 20),
    new THREE.MeshBasicMaterial({
      color: colour,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
      side: THREE.DoubleSide,
    }),
  );
  mesh.name = 'pointer-cursor';
  mesh.renderOrder = 999;
  mesh.visible = false;
  return mesh;
}

function makeControllerModel(colour: number): THREE.Group {
  const group = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.018, 0.09, 4, 10),
    new THREE.MeshStandardMaterial({ color: 0x23324a, roughness: 0.5, metalness: 0.3 }),
  );
  body.rotation.x = Math.PI / 2 - 0.5;
  body.position.set(0, -0.01, 0.03);
  group.add(body);
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.035, 0.005, 8, 24),
    new THREE.MeshStandardMaterial({
      color: colour,
      emissive: colour,
      emissiveIntensity: 0.5,
      roughness: 0.4,
    }),
  );
  ring.rotation.x = Math.PI / 2 - 0.3;
  ring.position.set(0, 0.015, -0.01);
  group.add(ring);
  return group;
}

/**
 * A gaze-and-pinch that began on something interactive and has been held long enough counts
 * as the grip. A pinch on open floor aims a teleport instead and never grips.
 */
export function holdBecomesGrip(
  p: Pick<Pointer, 'transient' | 'selecting' | 'squeezing' | 'pinchStart'>,
  now: number,
): boolean {
  return (
    !!p.transient &&
    p.selecting &&
    !p.squeezing &&
    !!p.pinchStart?.onItem &&
    now - p.pinchStart.at >= HOLD_TO_GRIP_MS
  );
}

export class InputManager {
  readonly pointers: Pointer[] = [];
  readonly mouse: Pointer;
  private slots: ControllerSlot[] = [];
  private listeners = new Map<PointerEventName, Set<PointerListener>>();
  private keyListeners = new Set<KeyListener>();
  private readonly ndc = new THREE.Vector2(0, 0);
  private readonly raycaster = new THREE.Raycaster();
  private mouseDown = false;
  private handFactory = new XRHandModelFactory();
  /** When true the mouse ray goes through the screen centre (pointer lock). */
  centreRay = false;
  readonly keysDown = new Set<string>();
  /**
   * True while a world-space text field owns the physical keyboard (no single-key shortcuts),
   * or a page overlay is open (`overlay`). Scenes set the text-field part every frame, so
   * the overlay keeps its own flag that they cannot clear.
   */
  get captureText(): boolean {
    return this.typingField || this.overlay;
  }
  set captureText(on: boolean) {
    this.typingField = on;
  }
  private typingField = false;
  /** A page overlay (the desktop controls list) holds the keys, as a text field does. */
  overlay = false;
  /** Mouse movement while the pointer is captured, consumed by locomotion as mouse look. */
  private readonly lookDelta = { x: 0, y: 0 };
  /**
   * Desktop, after onboarding: a click on empty space captures the mouse again (mouse look,
   * centre crosshair); with the mouse free, clicks on UI select it under the cursor.
   */
  lockOnClick = false;

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly rig: PlayerRig,
    private readonly canvas: HTMLCanvasElement,
  ) {
    this.mouse = {
      id: 'mouse',
      kind: 'mouse',
      handedness: 'none',
      ray: new THREE.Ray(),
      selecting: false,
      squeezing: false,
      axes: { x: 0, y: 0 },
      buttons: { a: false, b: false, stick: false, x: false },
      trigger: 0,
      grip: 0,
      active: !renderer.xr.isPresenting,
    };
    this.pointers.push(this.mouse);
    for (let i = 0; i < SLOTS; i++) this.setupSlot(i);
    this.setupDesktop();
  }

  on(event: PointerEventName, listener: PointerListener): () => void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(listener);
    return () => this.listeners.get(event)!.delete(listener);
  }

  onKey(listener: KeyListener): () => void {
    this.keyListeners.add(listener);
    return () => this.keyListeners.delete(listener);
  }

  private emit(event: PointerEventName, pointer: Pointer): void {
    for (const l of this.listeners.get(event) ?? []) l(pointer);
  }

  /**
   * Runs before a gaze-and-pinch select is announced. That pointer is created by the same
   * event that selects, before any frame has hit-tested it; the interactive system uses this
   * to find what the pinch is on (its hover state would otherwise be the previous pinch's).
   */
  beforeSelect(listener: PointerListener): void {
    this.selectPreparers.add(listener);
  }
  private readonly selectPreparers = new Set<PointerListener>();

  /** How many controller slots there are (hands and controllers share them). */
  get slotCount(): number {
    return this.slots.length;
  }

  private aimRay(slot: ControllerSlot): void {
    const p = slot.pointer;
    slot.controller.updateMatrixWorld(true);
    p.ray.origin.setFromMatrixPosition(slot.controller.matrixWorld);
    p.ray.direction.set(0, 0, -1).transformDirection(slot.controller.matrixWorld).normalize();
  }

  /* ---------- XR controllers and hands ---------- */

  private setupSlot(index: number): void {
    const controller = this.renderer.xr.getController(index);
    const grip = this.renderer.xr.getControllerGrip(index);
    const hand = this.renderer.xr.getHand(index);
    const colour = index === 0 ? 0x35b5b0 : 0x8fd3c8;
    const pointer: Pointer = {
      id: `xr-${index}`,
      kind: 'controller',
      handedness: 'none',
      ray: new THREE.Ray(),
      selecting: false,
      squeezing: false,
      axes: { x: 0, y: 0 },
      buttons: { a: false, b: false, stick: false, x: false },
      trigger: 0,
      grip: 0,
      active: false,
      line: makeRayLine(colour),
      cursor: makeCursor(colour),
    };
    controller.add(pointer.line!);
    const controllerModel = makeControllerModel(colour);
    controllerModel.visible = false;
    grip.add(controllerModel);
    const slot: ControllerSlot = {
      index,
      controller,
      grip,
      hand,
      pointer,
      inputSource: undefined,
      controllerModel,
      handModel: undefined,
    };
    this.slots.push(slot);
    this.pointers.push(pointer);
    this.rig.group.add(controller, grip, hand);
    this.rig.group.add(pointer.cursor!);

    controller.addEventListener('connected', (event) => {
      const source = (event as unknown as { data: XRInputSource }).data;
      slot.inputSource = source;
      pointer.handedness = (source.handedness as Pointer['handedness']) ?? 'none';
      pointer.kind = source.hand ? 'hand' : 'controller';
      pointer.transient = (source.targetRayMode as string) === 'transient-pointer';
      pointer.gamepad = !!source.gamepad && source.gamepad.buttons.length > 0;
      pointer.active = true;
      // A gaze ray starts between the eyes: no controller model and no ray line to draw.
      controllerModel.visible = !source.hand && !pointer.transient;
      if (pointer.line) pointer.line.visible = !pointer.transient;
      if (source.hand && !slot.handModel) {
        slot.handModel = this.handFactory.createHandModel(
          hand as unknown as Parameters<XRHandModelFactory['createHandModel']>[0],
          'mesh',
        );
        hand.add(slot.handModel);
      }
      if (slot.handModel) slot.handModel.visible = !!source.hand;
    });
    controller.addEventListener('disconnected', () => {
      slot.inputSource = undefined;
      // A pinch pointer can vanish mid-select: finish what it started.
      if (pointer.squeezing) {
        pointer.squeezing = false;
        this.emit('squeezeend', pointer);
      }
      if (pointer.selecting) {
        pointer.selecting = false;
        this.emit('selectend', pointer);
      }
      pointer.active = false;
      pointer.hitDistance = undefined;
      pointer.pinchStart = undefined;
      pointer.transient = false;
      controllerModel.visible = false;
      if (slot.handModel) slot.handModel.visible = false;
      if (pointer.cursor) pointer.cursor.visible = false;
    });
    controller.addEventListener('selectstart', () => {
      if (pointer.transient) {
        this.aimRay(slot);
        for (const prepare of this.selectPreparers) prepare(pointer);
        pointer.pinchStart = { at: performance.now(), onItem: pointer.hitDistance !== undefined };
      }
      pointer.selecting = true;
      this.emit('selectstart', pointer);
    });
    controller.addEventListener('selectend', () => {
      pointer.selecting = false;
      pointer.pinchStart = undefined;
      if (pointer.transient && pointer.squeezing) {
        pointer.squeezing = false;
        this.emit('squeezeend', pointer);
      }
      this.emit('selectend', pointer);
    });
    controller.addEventListener('squeezestart', () => {
      pointer.squeezing = true;
      this.emit('squeezestart', pointer);
    });
    controller.addEventListener('squeezeend', () => {
      pointer.squeezing = false;
      this.emit('squeezeend', pointer);
    });
  }

  /* ---------- Desktop ---------- */

  private setupDesktop(): void {
    this.canvas.addEventListener('pointermove', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      this.ndc.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
    });
    document.addEventListener('mousemove', (e) => {
      if (document.pointerLockElement !== this.canvas || this.renderer.xr.isPresenting) return;
      this.lookDelta.x += e.movementX;
      this.lookDelta.y += e.movementY;
    });
    this.canvas.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || this.renderer.xr.isPresenting) return;
      if (
        this.lockOnClick &&
        !document.pointerLockElement &&
        this.mouse.hitDistance === undefined
      ) {
        // Nothing under the free cursor: take the mouse back for looking around.
        this.requestLock();
        return;
      }
      this.mouseDown = true;
      this.mouse.selecting = true;
      this.emit('selectstart', this.mouse);
    });
    window.addEventListener('pointerup', (e) => {
      if (e.button !== 0 || !this.mouseDown) return;
      this.mouseDown = false;
      this.mouse.selecting = false;
      this.emit('selectend', this.mouse);
    });
    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      this.keysDown.add(e.code);
      for (const l of this.keyListeners) l(e);
    });
    window.addEventListener('keyup', (e) => this.keysDown.delete(e.code));
    window.addEventListener('blur', () => this.keysDown.clear());
    // Locked: the ray goes through the screen centre. Released (Esc): it follows the cursor,
    // which is how a desktop user clicks the wrist menu and other UI in front of them.
    document.addEventListener('pointerlockchange', () => {
      this.centreRay = !!document.pointerLockElement && !this.renderer.xr.isPresenting;
    });
  }

  /** Mouse-look movement since the last call, in pixels. */
  consumeLook(): { x: number; y: number } {
    const out = { x: this.lookDelta.x, y: this.lookDelta.y };
    this.lookDelta.x = 0;
    this.lookDelta.y = 0;
    return out;
  }

  /** Captures the mouse (desktop); browsers refuse silently without a recent user gesture. */
  requestLock(): void {
    if (this.renderer.xr.isPresenting) return;
    try {
      const r = this.canvas.requestPointerLock?.() as unknown;
      if (r instanceof Promise) r.catch(() => {});
    } catch {
      /* not allowed right now */
    }
  }

  /* ---------- Per frame ---------- */

  update(): void {
    const presenting = this.renderer.xr.isPresenting;
    this.mouse.active = !presenting;
    if (!presenting) {
      this.raycaster.setFromCamera(
        this.centreRay ? new THREE.Vector2(0, 0) : this.ndc,
        this.rig.camera,
      );
      this.mouse.ray.copy(this.raycaster.ray);
    }
    for (const slot of this.slots) {
      const p = slot.pointer;
      if (!p.active) continue;
      this.aimRay(slot);
      if (holdBecomesGrip(p, performance.now())) {
        p.squeezing = true;
        this.emit('squeezestart', p);
      }
      const gamepad = slot.inputSource?.gamepad;
      if (gamepad) {
        p.axes.x = gamepad.axes[2] ?? gamepad.axes[0] ?? 0;
        p.axes.y = gamepad.axes[3] ?? gamepad.axes[1] ?? 0;
        p.buttons.stick = !!gamepad.buttons[3]?.pressed;
        p.buttons.a = !!gamepad.buttons[4]?.pressed;
        p.buttons.b = !!gamepad.buttons[5]?.pressed;
        p.buttons.x = !!gamepad.buttons[4]?.pressed && p.handedness === 'left';
        p.trigger = gamepad.buttons[0]?.value ?? 0;
        p.grip = gamepad.buttons[1]?.value ?? 0;
      } else {
        p.axes.x = p.axes.y = 0;
      }
      if (p.kind === 'hand') {
        const joints = (slot.hand as unknown as { joints?: Record<string, THREE.Object3D> }).joints;
        const tip = joints?.['index-finger-tip'];
        if (tip) {
          p.indexTip ??= new THREE.Vector3();
          tip.getWorldPosition(p.indexTip);
        }
      }
      if (p.line)
        p.line.scale.z =
          p.hitDistance !== undefined ? Math.min(p.hitDistance, RAY_LENGTH) : RAY_LENGTH;
      if (p.cursor) {
        if (p.hitDistance !== undefined) {
          p.cursor.visible = true;
          const hit = p.ray.at(p.hitDistance, new THREE.Vector3());
          this.rig.group.worldToLocal(hit);
          p.cursor.position.copy(hit);
          p.cursor.lookAt(this.rig.group.worldToLocal(p.ray.origin.clone()));
        } else {
          p.cursor.visible = false;
        }
      }
    }
  }

  /**
   * World pose of a hand for the pose broadcast: the controller grip space, or the wrist
   * joint when hand tracking is active, plus pinch and per-finger curl.
   */
  handPose(index: number): HandPoseSample | undefined {
    const slot = this.slots[index];
    if (!slot || !slot.pointer.active || !slot.inputSource) return undefined;
    // A gaze-and-pinch pointer is not a hand: its grip space only exists mid-pinch.
    if (slot.pointer.transient) return undefined;
    const p = slot.pointer;
    const out: HandPoseSample = {
      handedness: p.handedness,
      kind: p.kind === 'hand' ? 'hand' : 'controller',
      position: new THREE.Vector3(),
      quaternion: new THREE.Quaternion(),
      pinch: 0,
      curl: [0, 0, 0, 0, 0],
      pointerId: p.id,
    };
    if (p.kind === 'hand') {
      const joints = (slot.hand as unknown as { joints?: Record<string, THREE.Object3D> }).joints;
      const wrist = joints?.['wrist'];
      if (!wrist) return undefined;
      wrist.updateWorldMatrix(true, false);
      wrist.getWorldPosition(out.position);
      wrist.getWorldQuaternion(out.quaternion);
      const tipOf = (name: string) => joints?.[name]?.getWorldPosition(new THREE.Vector3());
      const thumbTip = tipOf('thumb-tip');
      const indexTip = tipOf('index-finger-tip');
      if (thumbTip && indexTip)
        out.pinch = THREE.MathUtils.clamp(1 - (thumbTip.distanceTo(indexTip) - 0.015) / 0.05, 0, 1);
      if (thumbTip) out.thumbTip = thumbTip;
      const fingers = ['thumb', 'index-finger', 'middle-finger', 'ring-finger', 'pinky-finger'];
      fingers.forEach((f, i) => {
        const tip = tipOf(`${f}-tip`);
        const base = tipOf(f === 'thumb' ? 'thumb-metacarpal' : `${f}-metacarpal`);
        if (!tip || !base) return;
        const straight = f === 'thumb' ? 0.12 : 0.17;
        out.curl[i] = THREE.MathUtils.clamp(
          1 - (tip.distanceTo(base) - 0.05) / (straight - 0.05),
          0,
          1,
        );
      });
    } else {
      slot.grip.updateWorldMatrix(true, false);
      slot.grip.getWorldPosition(out.position);
      slot.grip.getWorldQuaternion(out.quaternion);
      out.pinch = p.trigger;
      const gamepad = slot.inputSource.gamepad;
      const thumbTouched = !!gamepad && [3, 4, 5].some((i) => gamepad.buttons[i]?.touched);
      out.curl = [thumbTouched ? 0.6 : 0.1, p.trigger, p.grip, p.grip, p.grip];
    }
    return out;
  }

  /** Object to hang the wrist menu from: left grip or left wrist joint. */
  leftWrist(): THREE.Object3D | undefined {
    const slot = this.slots.find(
      (s) => s.pointer.active && !s.pointer.transient && s.pointer.handedness === 'left',
    );
    if (!slot) return undefined;
    if (slot.pointer.kind === 'hand') {
      const joints = (slot.hand as unknown as { joints?: Record<string, THREE.Object3D> }).joints;
      return joints?.['wrist'];
    }
    return slot.grip;
  }

  /** The pointer that should drive UI on desktop, or all active XR pointers. */
  activePointers(): Pointer[] {
    return this.pointers.filter((p) => p.active);
  }

  /** Hide or show the XR ray visuals (e.g. while teleport-aiming). */
  setRayVisible(pointer: Pointer, visible: boolean): void {
    if (pointer.line) pointer.line.visible = visible && !pointer.transient;
  }
}
